/**
 * Бюджетные справочники сезона — экран 1 годового планирования.
 *
 * Макет (утверждён founder 15.08):
 *   `docs/superpowers/prototypes/annual-planning/v2-01-budget-catalogs.html`
 *   (ветка `docs/wireframes-season-planning-v2`, скриншот рядом).
 * Правки founder дословно:
 *   `docs/superpowers/specs/2026-08-15-budget-catalogs-founder-edits.md`.
 *
 * Что экран обещает пользователю: у бюджета сезона свой состав позиций и свои
 * плановые цены. Правки здесь НЕ меняют справочник хозяйства и НЕ трогают
 * факт прошлого сезона — поэтому «убрать из бюджета», а не «удалить».
 *
 * Разрезы ведения (порции 3-4 founder):
 *   • ТМЦ (СЗР / Семена / Удобрения / ГСМ / Прочее) — на компанию, с опцией
 *     цены для конкретного хозяйства строкой-исключением;
 *   • расценки работ, аутсорс, готовая продукция — по хозяйству; строка без
 *     хозяйства = шаблон компании, из которого копируют.
 *
 * Мастер наполнения (эксель / прошлый бюджет / справочники компании) живёт
 * в `FillWizard.tsx` по макету v2-13 и открывается кнопкой «Наполнить».
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import { useParams } from '@tanstack/react-router';
import { Plus, Search, Wand2 } from 'lucide-react';
import { AppShell } from '@/components/layout/AppShell';
import { PageHeader } from '@/components/layout/PageHeader';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';
import { Stack } from '@/components/ui/stack';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { usePlanningFarmScope } from '@/components/scope/usePlanningFarmScope';
import { useCan } from '@/lib/auth/usePermissions';
import { useSeasonByYear } from '@/lib/planning/useSeasonByYear';
import { useFarms } from '@/lib/reference/catalogScopes';
import { matchesSearch, SEARCH_PLACEHOLDER } from '@/lib/reference/normalizeSearch';
import {
  FARM_LEVEL_SECTIONS,
  SECTION_TITLES,
  TMC_SECTIONS,
  isFarmLevel,
  useArchiveBudgetItem,
  useBudgetSection,
  useBudgetSectionCounts,
  useCreateBudgetItem,
  useDeleteFarmPrice,
  usePatchBudgetItem,
  useUpsertFarmPrice,
  budgetErrorText,
  type BudgetItem,
  type BudgetSection,
} from '@/lib/planning/budgetCatalog';
import { findCatalog, useSeasonCatalogs } from '@/lib/planning/seasonCatalogApproval';
import { toast } from '@/lib/toast';
import { SeasonNotStartedCard, SeasonYearOutOfWindowCard } from '../SeasonBudgetStates';
import { ApprovedEditWarning, CatalogApprovalBar } from './CatalogApprovalBar';
import {
  BudgetItemDialog,
  toCreateBody,
  toPatchBody,
  type BudgetItemDialogSubmit,
} from './BudgetItemDialog';
import { FarmPriceDialog } from './FarmPriceDialog';
import { FarmScopeBar, type BudgetLevel } from './FarmScopeBar';
import { BudgetItemsTable } from './BudgetItemsTable';
import { cn } from '@/lib/utils';
import { NormsTree } from './NormsTree';
import { FillWizard } from './FillWizard';

/**
 * Вкладки бюджетного справочника. Цен ГОТОВОЙ ПРОДУКЦИИ здесь нет намеренно
 * (AK-769): они ведутся на экране «Цены и объёмы реализации» (матрица культура
 * × месяц, канон `company_sale_prices`) — именно оттуда их читает план по
 * выручке. Вкладка-двойник давала вторую дверь, правка в которой до выручки не
 * доезжала: экономист вводил цену, а выручка и маржа оставались «н/д».
 */
type TabKey = 'tmc' | 'work_rate' | 'outsource';

const TAB_TITLES: Record<TabKey, string> = {
  tmc: 'ТМЦ и плановые цены',
  work_rate: 'Расценки работ и топливо',
  outsource: 'Аутсорс',
};

/**
 * Вкладка ↔ вид сезонного справочника статус-машины (зеркало
 * `_SECTION_CATALOG_KIND` в `api/v1/budget_catalog.py`). ТМЦ всегда на уровне
 * компании — закупки централизованные (ADR-065 порция 3-4), у остальных трёх
 * уровень ведения совпадает с выбранным хозяйством/шаблоном экрана.
 */
const TAB_CATALOG_KIND: Record<TabKey, string> = {
  tmc: 'tmc_prices',
  work_rate: 'operations_rates',
  outsource: 'outsource',
};

export function BudgetCatalogsPage() {
  const params = useParams({ strict: false }) as { year?: string };
  const year = Number(params.year);

  const [tab, setTab] = useState<TabKey>('tmc');
  const [tmcSection, setTmcSection] = useState<BudgetSection>('pesticide');
  // Уровень ведения: шаблон компании или хозяйство из переключателя слева
  // (AK-866 В-3: область — из переключателя; своего выбора хозяйства на
  // экране нет, AK-897). Хозяйство сменили в переключателе — уровень «хозяйство»
  // сам следует за ним.
  const [level, setLevel] = useState<BudgetLevel>('template');
  const farmScope = usePlanningFarmScope({ onScopeChanged: () => undefined });
  const farmId = level === 'farm' ? farmScope.farmId : null;
  const [term, setTerm] = useState('');
  const [showArchived, setShowArchived] = useState(false);
  const [fillOpen, setFillOpen] = useState(false);
  const [editing, setEditing] = useState<{ item: BudgetItem | null } | null>(null);
  const [farmPriceFor, setFarmPriceFor] = useState<{ item: BudgetItem; farmId: string | null } | null>(
    null,
  );

  const section: BudgetSection = tab === 'tmc' ? tmcSection : tab;
  const farmLevel = isFarmLevel(section);
  // Расценки работ founder читает КНИГОЙ, а не прайсом: дерево «работа →
  // параметры → тягач → орудие → ширина → цифры» — это его файл ЕТС один в
  // один. Поэтому для расценок дерево — вид по умолчанию, а плоский список
  // остаётся под переключателем: в нём живут правки цен и статусы.
  // Вид по умолчанию зависит от скоупа, и это не компромисс, а смысл: книга
  // нормативов ведётся на уровне ХОЛДИНГА, поэтому деревом она открывается в
  // шаблоне компании. В разрезе хозяйства экономист работает со СВОИМИ ценами-
  // переопределениями — там список, иначе экран показывал бы общую книгу
  // вместо того, ради чего человек выбрал хозяйство. Переключатель есть в обоих.
  const bookSection = section === 'work_rate';
  const [workRateView, setWorkRateView] = useState<'tree' | 'list' | null>(null);
  const showTree = bookSection && (workRateView ?? (farmId ? 'list' : 'tree')) === 'tree';
  const catalogKind = TAB_CATALOG_KIND[tab];
  // ТМЦ утверждается на уровне компании всегда; у остальных трёх — уровень
  // экрана (шаблон компании farmId=null, либо выбранное хозяйство).
  const catalogFarmId = farmLevel ? farmId : null;

  const canCreate = useCan('budget_catalog', 'c');
  const canWrite = useCan('budget_catalog', 'u');
  const canArchive = useCan('budget_catalog', 'd');
  // Утверждение справочника — ОТДЕЛЬНОЕ право от правки позиций (RBAC-ресурс
  // `season_catalog_approval`, ADR-053/SA-03): экономист может вести
  // справочник, не будучи тем, кто утверждает бюджет.
  const canApproveCatalog = useCan('season_catalog_approval', 'u');

  // Сезон резолвим по году из url — общий хелпер с плановыми ценами и
  // техкартами (`useSeasonByYear`): «сезона нет» должно отвечаться одинаково.
  const season = useSeasonByYear(year);
  const seasonId = season.seasonId;
  const seasonsLoading = season.isLoading;
  const seasonMissing = season.missing;

  // Два источника хозяйств на экране — намеренно, у них разная работа:
  //   • область сессии (`/scope/available`, переключатель слева) задаёт
  //     хозяйство уровня ведения и его подпись;
  //   • `/farms` (плоский список тенанта) остаётся источником для диалога
  //     «своя цена хозяйства» — это ввод цен по хозяйствам, а не контекст
  //     экрана, и правила видимости в нём этот PR не меняет.
  const farms = useFarms();
  const farmName = farmId ? (farmScope.farm?.name ?? null) : null;

  // «Взять из прошлого бюджета» — только сезоны СТАРШЕ текущего: копировать
  // вперёд из будущего бессмысленно, а пустой список гасит способ в мастере.
  const priorSeasons = useMemo(
    () =>
      season.seasons
        // Незаведённый сезон копировать не из чего: `season_id` у него null
        // (ADR-060 §S6) — такой год в списке источников только обманывает.
        .filter((s) => s.year < year && !!s.season_id)
        .sort((a, b) => b.year - a.year)
        .map((s) => ({ season_id: s.season_id as string, year: s.year })),
    [season.seasons, year],
  );

  const listParams = {
    seasonId,
    section,
    farmId: farmLevel ? farmId : null,
    includeArchived: showArchived,
  };
  const list = useBudgetSection(listParams);
  const { counts: tmcCounts, isLoading: tmcCountsLoading } = useBudgetSectionCounts(
    tab === 'tmc' ? TMC_SECTIONS : [],
    { seasonId, farmId: null, includeArchived: showArchived },
  );

  const catalogsQuery = useSeasonCatalogs(seasonId, catalogFarmId);
  const catalog = findCatalog(catalogsQuery.data, catalogKind);

  // «Пусто ли» для диалога «в справочнике нет позиций — точно утвердить?»
  // (кнопка утверждения). ТМЦ = сумма по пяти подвкладкам (один catalog_kind
  // на все пять), у остальных трёх один список = один catalog_kind, честнее
  // смотреть на `list.data` (весь ответ сервера), а не на `rows` — те урезаны
  // поисковой строкой, и «нет результатов поиска» не значит «справочник пуст».
  const catalogItemCount =
    tab === 'tmc'
      ? Object.values(tmcCounts).reduce((sum, n) => sum + (n ?? 0), 0)
      : (list.data?.length ?? 0);
  // Пока позиции ещё грузятся, `catalogItemCount` честно 0, но это НЕ
  // «справочник пуст» — быстрый клик по кнопке утверждения в этот момент
  // ловил бы ложный диалог «нет позиций» (head-web ревью #850, follow-up).
  // Гейт утверждения обязан ждать оба источника: статус справочника И сами
  // позиции, а не только `catalogsQuery`.
  //
  // На ТМЦ «позиции» — это ПЯТЬ запросов (`tmcCounts`), а `list` — лишь
  // активная подвкладка. Ждать только его мало: СЗР приехал пустым, «Семена»
  // с сотней позиций ещё в пути — сумма честно 0, кнопка уже на экране, клик
  // ловит ложный диалог «нет позиций» (head-web ревью #850, AK-707).
  const catalogGateLoading =
    catalogsQuery.isLoading || list.isLoading || (tab === 'tmc' && tmcCountsLoading);

  // Тост «утверждение снято» — ЕДИНСТВЕННОЕ место, которое видит реальный
  // переход `approved -> revised`, пришедший от сервера после ЛЮБОЙ правки
  // позиции (правка снимает утверждение автоматически на бэке,
  // `season_planning.mark_catalog_in_progress`, независимо от того, какой
  // именно контрол её вызвал — точечная правка цены, диалог, импорт,
  // «убрать из бюджета»…). Централизованно здесь, а не в каждом write-хендлере.
  //
  // ⚠️ Ключ прошлого состояния — ПАРА (какой каталог, какой статус), не
  // просто статус. Head-web ревью #850: без этого переключение вкладки
  // «ТМЦ» (approved) → «Расценки работ» (уже revised до нас) читалось как
  // «правка сняла утверждение» и лгало тостом на пустом месте — catalog
  // меняется от смены catalogKind/catalogFarmId ровно так же, как от write.
  const catalogIdentity = `${catalogKind}:${catalogFarmId ?? 'company'}`;
  const prevCatalogRef = useRef<{ identity: string; status: string | undefined } | undefined>(undefined);
  useEffect(() => {
    const prev = prevCatalogRef.current;
    if (prev && prev.identity === catalogIdentity && prev.status === 'approved' && catalog?.status === 'revised') {
      // info, не warning: warning в этом тостере sticky (висит до ручного
      // закрытия) — на каждую правку утверждённого справочника пользователь
      // ловил бы лишний клик «закрыть» (head-web ревью #850).
      toast.info(
        `Справочник «${TAB_TITLES[tab]}» был утверждён — правка автоматически сняла утверждение (статус «Пересмотрен»).`,
      );
    }
    prevCatalogRef.current = { identity: catalogIdentity, status: catalog?.status };
  }, [catalogIdentity, catalog?.status, tab]);

  const rows = useMemo(() => {
    let data = list.data ?? [];
    // Шаблон компании: бэк без `farm_id` отдаёт все строки раздела, включая
    // чужие хозяйства — оставляем только строки без хозяйства.
    if (farmLevel && !farmId) data = data.filter((it) => it.farm_id === null);
    return data
      .filter((it) => matchesSearch(it.name, term))
      .sort((a, b) => {
        if (!!a.archived_at !== !!b.archived_at) return a.archived_at ? 1 : -1;
        return a.name.localeCompare(b.name, 'ru');
      });
  }, [list.data, farmLevel, farmId, term]);

  const create = useCreateBudgetItem(section);
  const patch = usePatchBudgetItem(section);
  const archive = useArchiveBudgetItem(section);
  const upsertFarmPrice = useUpsertFarmPrice();
  const deleteFarmPrice = useDeleteFarmPrice();

  function submitItem(values: BudgetItemDialogSubmit) {
    if (!seasonId || !editing) return;
    const done = () => {
      setEditing(null);
      create.reset();
      patch.reset();
    };
    if (editing.item) {
      patch.mutate({ id: editing.item.id, patch: toPatchBody(values) }, { onSuccess: done });
    } else {
      create.mutate(toCreateBody(values, seasonId, farmLevel ? farmId : null), { onSuccess: done });
    }
  }

  /**
   * «Взять в хозяйство» — строка шаблона компании копируется в выбранное
   * хозяйство с той же ценой, и дальше правится ТАМ. Правка самого шаблона
   * из разреза хозяйства запрещена: она молча меняла бы цифры всем
   * хозяйствам (вердикт head-web по #783, блокер 1).
   */
  function adoptTemplateRow(item: BudgetItem) {
    if (!seasonId || !farmId) return;
    create.mutate({
      season_id: seasonId,
      farm_id: farmId,
      name: item.name,
      item_kind: item.item_kind,
      grade: item.grade,
      unit_id: item.unit_id,
      planned_price: item.planned_price,
      price_source: item.price_source,
      ref_id: item.ref_id,
      notes: item.notes,
    });
  }

  const validYear = season.validYear;
  const title = validYear ? `Бюджетные справочники сезона ${year}` : 'Бюджетные справочники';

  /**
   * «Наполнить справочники» без сезона — молчала (AK-648, UAT 18.08): мастер
   * рендерится по `fillOpen && seasonId`, поэтому клик взводил флаг и на
   * экране не происходило НИЧЕГО. Кнопка обязана либо работать, либо сказать
   * почему не работает.
   */
  function openFillWizard() {
    if (seasonId) {
      setFillOpen(true);
      return;
    }
    toast.error(
      seasonMissing
        ? `Сезона ${year} нет в списке сезонов компании — проверьте окно сезонов в настройках.`
        : `Сначала откройте бюджет сезона ${year} — кнопка на этой странице.`,
    );
  }

  return (
    <AppShell>
      <div className="mx-auto w-full max-w-[1600px] pb-safe-fab">
        <PageHeader
          title={title}
          showBack
          breadcrumbs={[
            { label: 'Планирование сезона', to: '/planning' },
            // Средняя крошка ведёт в хаб бюджета сезона (макет v2-00) — цепочка
            // «Сезоны → Бюджет сезона → раздел» кликабельна на каждом звене.
            ...(validYear
              ? [
                  {
                    label: `Бюджет сезона ${year}`,
                    to: `/planning/seasons/${year}/budget`,
                  },
                ]
              : []),
            { label: 'Бюджетные справочники' },
          ]}
          description="У бюджета свой состав позиций и свои плановые цены. Правки здесь не меняют справочник хозяйства и не трогают факт прошлого сезона."
          actions={
            <Button variant="outline" onClick={openFillWizard} disabled={seasonsLoading}>
              <Wand2 className="mr-2 h-4 w-4" />
              Наполнить справочники
            </Button>
          }
        />

        <Stack gap={4} className="mt-4">
          {!validYear && (
            <Card>
              <CardContent className="p-4 text-sm text-destructive">
                Год в адресе страницы некорректный.
              </CardContent>
            </Card>
          )}

          {season.validYear && seasonsLoading && <Skeleton className="h-96 w-full" />}

          {season.validYear && season.isError && (
            <Card>
              <CardContent className="p-4 text-sm text-destructive">
                Не удалось загрузить сезоны. Обновите страницу.
              </CardContent>
            </Card>
          )}

          {/* Два РАЗНЫХ отказа вместо одного текста «откройте на странице
              Планирование» (там кнопки не было — юзер ходил по кругу). */}
          {seasonMissing && <SeasonYearOutOfWindowCard year={year} />}
          {season.notOpened && (
            // Остаёмся на месте: инвалидация карточек сезонов перечитает
            // `season_id`, и экран оживает прямо тут, без перехода.
            <SeasonNotStartedCard year={year} onOpened={() => undefined} />
          )}

          {seasonId && (
            <>
              <Tabs value={tab} onValueChange={(v) => setTab(v as TabKey)}>
                <TabsList>
                  {(Object.keys(TAB_TITLES) as TabKey[]).map((key) => (
                    <TabsTrigger key={key} value={key}>
                      {TAB_TITLES[key]}
                      {key !== 'tmc' && FARM_LEVEL_SECTIONS.includes(key) && (
                        <span className="ml-2 text-xs text-muted-foreground">по хозяйству</span>
                      )}
                    </TabsTrigger>
                  ))}
                </TabsList>
              </Tabs>

              {tab === 'tmc' && (
                <div className="flex flex-wrap items-center gap-2">
                  {TMC_SECTIONS.map((s) => (
                    <button
                      key={s}
                      type="button"
                      aria-pressed={s === tmcSection}
                      onClick={() => setTmcSection(s)}
                      className={
                        s === tmcSection
                          ? 'rounded-full border border-primary bg-primary/10 px-3 py-1 text-sm font-medium text-primary transition-colors'
                          : 'rounded-full border border-border px-3 py-1 text-sm text-muted-foreground transition-colors hover:bg-accent hover:text-foreground'
                      }
                    >
                      {SECTION_TITLES[s]}
                      {tmcCounts[s] !== undefined && (
                        <span className="ml-1 text-xs">{tmcCounts[s]}</span>
                      )}
                    </button>
                  ))}
                  <span className="text-xs text-muted-foreground">
                    одна категория — один список
                  </span>
                </div>
              )}

              {farmLevel && (
                <FarmScopeBar
                  farm={farmScope.farm}
                  value={farmId ? 'farm' : 'template'}
                  onChange={setLevel}
                  section={section}
                  loading={farmScope.status === 'loading'}
                />
              )}

              <Card>
                <CardContent className="p-4">
                  <div className="flex flex-wrap items-center gap-2">
                    <h2 className="text-base font-semibold text-foreground">
                      {SECTION_TITLES[section]}
                    </h2>
                    {farmLevel && (
                      <Badge variant="outline">
                        {farmId ? `хозяйство: ${farmName ?? '—'}` : 'шаблон компании'}
                      </Badge>
                    )}
                    {canCreate && (
                      <Button size="sm" onClick={() => setEditing({ item: null })}>
                        <Plus className="mr-2 h-4 w-4" />
                        Добавить позицию
                      </Button>
                    )}
                    <button
                      type="button"
                      aria-pressed={showArchived}
                      onClick={() => setShowArchived((v) => !v)}
                      className={
                        showArchived
                          ? 'rounded-full border border-primary bg-primary/10 px-3 py-1 text-sm text-primary transition-colors'
                          : 'rounded-full border border-border px-3 py-1 text-sm text-muted-foreground transition-colors hover:bg-accent hover:text-foreground'
                      }
                    >
                      Убранные из бюджета
                    </button>
                    <div className="relative ml-auto w-72">
                      <Search className="absolute left-2 top-2 h-4 w-4 text-muted-foreground" />
                      <Input
                        value={term}
                        onChange={(e) => setTerm(e.target.value)}
                        placeholder={SEARCH_PLACEHOLDER}
                        className="pl-8"
                        aria-label="Поиск позиции"
                      />
                    </div>
                  </div>

                  {bookSection && (
                    <div className="mt-3 flex gap-2">
                      {(
                        [
                          ['tree', 'Книгой'],
                          ['list', 'Списком'],
                        ] as const
                      ).map(([kind, title]) => (
                        <button
                          key={kind}
                          type="button"
                          aria-pressed={(workRateView ?? (farmId ? 'list' : 'tree')) === kind}
                          onClick={() => setWorkRateView(kind)}
                          className={cn(
                            'rounded-full border px-3 py-1 text-sm',
                            (workRateView ?? (farmId ? 'list' : 'tree')) === kind
                              ? 'border-primary bg-accent text-foreground'
                              : 'border-border text-muted-foreground hover:bg-muted',
                          )}
                        >
                          {title}
                        </button>
                      ))}
                    </div>
                  )}

                  <p className="mt-2 text-sm text-muted-foreground">
                    {showTree
                      ? 'Нормативы ЕТС как в книге: работа → параметры → трактор → машина → ширина. На листе — цифры строки. Цена работы задаётся на уровне параметров, ниже книга только читается.'
                      : farmLevel
                      ? 'Расценки и цены ведутся по хозяйству: уровень компании — шаблон, из которого копируют, а не место правды.'
                      : 'Закупки ведутся на компанию: плановая цена едина для всех хозяйств. Своя цена хозяйства заводится строкой-исключением.'}
                  </p>

                  {seasonId && (
                    <div className="mt-3">
                      <CatalogApprovalBar
                        seasonId={seasonId}
                        catalogKind={catalogKind}
                        farmId={catalogFarmId}
                        catalog={catalog}
                        canApprove={canApproveCatalog}
                        isLoading={catalogGateLoading}
                        isEmpty={catalogItemCount === 0}
                      />
                    </div>
                  )}
                  <div className="mt-2">
                    <ApprovedEditWarning catalog={catalog} />
                  </div>

                  <div className="mt-4">
                    {list.isLoading && <Skeleton className="h-48 w-full" />}

                    {list.isError && (
                      <p className="text-sm text-destructive">
                        {budgetErrorText(list.error)}
                      </p>
                    )}

                    {!list.isLoading && !list.isError && rows.length === 0 && (
                      <div className="p-8 text-center">
                        <p className="text-sm text-foreground">
                          {term
                            ? 'По этому запросу ничего не нашлось.'
                            : `В бюджете сезона ${year} этот раздел пока пуст.`}
                        </p>
                        {!term && (
                          <div className="mt-3 flex flex-wrap items-center justify-center gap-2">
                            <Button onClick={() => setFillOpen(true)}>
                              <Wand2 className="mr-2 h-4 w-4" />
                              Наполнить справочник
                            </Button>
                            {canCreate && (
                              <Button variant="outline" onClick={() => setEditing({ item: null })}>
                                <Plus className="mr-2 h-4 w-4" />
                                Добавить позицию вручную
                              </Button>
                            )}
                          </div>
                        )}
                      </div>
                    )}

                    {showTree && <NormsTree seasonId={seasonId} canWrite={canWrite} />}

                    {!showTree && !list.isLoading && !list.isError && rows.length > 0 && (
                      <BudgetItemsTable
                        rows={rows}
                        section={section}
                        listUpdatedAt={list.dataUpdatedAt}
                        priceScopeLabel={
                          farmLevel
                            ? farmId
                              ? (farmName ?? 'хозяйство')
                              : 'шаблон компании'
                            : 'компания'
                        }
                        templateReadOnly={farmLevel && !!farmId}
                        canWrite={canWrite}
                        canCreate={canCreate}
                        canArchive={canArchive}
                        onEdit={(item) => setEditing({ item })}
                        onArchive={(item) =>
                          archive.mutate({ id: item.id, restore: !!item.archived_at })
                        }
                        onPriceCommit={(item, value) =>
                          patch.mutateAsync({ id: item.id, patch: { planned_price: value } })
                        }
                        onAdoptTemplate={adoptTemplateRow}
                        onAddFarmPrice={(item) => setFarmPriceFor({ item, farmId: null })}
                        onEditFarmPrice={(item, farm) => setFarmPriceFor({ item, farmId: farm })}
                        onDeleteFarmPrice={(item, farm) =>
                          deleteFarmPrice.mutate({ id: item.id, farmId: farm })
                        }
                      />
                    )}
                  </div>
                </CardContent>
              </Card>
            </>
          )}
        </Stack>
      </div>

      {fillOpen && seasonId && (
        <FillWizard
          section={section}
          seasonId={seasonId}
          farmId={farmLevel ? farmId : null}
          farmName={farmLevel ? farmName : null}
          priorSeasons={priorSeasons}
          canCreate={canCreate}
          canWrite={canWrite}
          onManual={() => setEditing({ item: null })}
          onClose={() => setFillOpen(false)}
        />
      )}

      {editing && seasonId && (
        <BudgetItemDialog
          section={section}
          item={editing.item}
          farmId={farmLevel ? farmId : null}
          farmName={farmName}
          saving={create.isPending || patch.isPending}
          onSubmit={submitItem}
          onClose={() => {
            setEditing(null);
            create.reset();
            patch.reset();
          }}
        />
      )}

      {farmPriceFor && (
        <FarmPriceDialog
          item={farmPriceFor.item}
          farms={farms.data ?? []}
          initialFarmId={farmPriceFor.farmId}
          saving={upsertFarmPrice.isPending}
          onSubmit={(values) =>
            upsertFarmPrice.mutate(
              { id: farmPriceFor.item.id, body: values },
              {
                onSuccess: () => {
                  setFarmPriceFor(null);
                  upsertFarmPrice.reset();
                },
              },
            )
          }
          onClose={() => {
            setFarmPriceFor(null);
            upsertFarmPrice.reset();
          }}
        />
      )}
    </AppShell>
  );
}
